import type {ReactElement} from 'react';
import {useContext} from 'react';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerBottomSheet} from '@EmailMakerReact/components/email-maker-bottom-sheet';
import {useCloseSheetOnCanvasInteraction} from '@EmailMakerReact/hooks/use-close-sheet-on-canvas-interaction';
import {ColorPickerView} from '@EmailMakerReact/layout/email-maker-right-panel/views/color-picker-view/color-picker-view';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './email-maker-color-picker-sheet.module.scss';

const PANEL_ID = 'email-maker-color-picker-sheet-panel';

/** Mobile/tablet counterpart of the desktop COLOR_PICKER right-panel overlay. Pre-mounted inside the editor context so the editor and color target are non-null when the sheet opens; reads the target the swatch set via actions.setColorPickerTarget. */
export function EmailMakerColorPickerSheet(): ReactElement | null {
  const {editor, state, actions} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET]?.isOpen ?? false);

  const target = state.colorPickerTarget;
  const selected = state.selectedComponent;

  useCloseSheetOnCanvasInteraction(isOpen ? editor : null, MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET, {
    skipSelectedCid: selected?.cid,
  });

  if (!isOpen || !editor || !target) {
    return null;
  }

  function handleBack(): void {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET));
    actions.setColorPickerTarget(null);
  }

  // Close dismisses the whole flow: this picker plus the mobile control sheet it was opened from.
  function handleClose(): void {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET));
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_MOBILE_CONTROL_MODAL));
    actions.setColorPickerTarget(null);
  }

  return (
    <EmailMakerBottomSheet modalId={MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET} panelId={PANEL_ID} showDragHandle={false}>
      <div className={styles.sheet}>
        <ColorPickerView editor={editor} target={target} onBack={handleBack} onClose={handleClose} />
      </div>
    </EmailMakerBottomSheet>
  );
}
